import React from 'react';
import styled from 'styled-components';
import { Body7 } from '@ui';
import { GhostButton } from '@ui/buttons';
import {
  aggregatedMarkerDescription,
  EM_DASH,
  formatFeedId,
  formatMarker,
  formatScore,
  inputValueTint,
  MARKER_DESCRIPTIONS,
  MARKER_TOKENS,
} from './format';
import { MarkerDot } from './markerStyles';
import { washBackground, washCellCss, WashCell } from './valueWash';

// Inset ring (-1): this fills a grid cell, so an outside ring would sit over its neighbour.
const ValueButton = styled(GhostButton)`
  ${washCellCss};
  ${washBackground};
  /* Transparent rather than absent: it reserves the space the hover border fills, so a marker does
     not shift 2px under the pointer. */
  border: 1px solid transparent;

  &:hover {
    border-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  }

  &:focus-visible {
    outline-offset: -1px;
  }
`;

// A member that arrived without a value has nothing to wash, so it stays quiet type: the grid's
// colour should mean "this is a number", not "this cell exists".
const StatusText = styled(Body7)`
  padding-left: 8px;
`;

const StatusButton = styled(GhostButton)`
  padding: 0 0 0 8px;

  &:focus-visible {
    outline-offset: 1px;
  }
`;

// One per-input transparency marker.
//
// While no drill-down handler is wired this is not a control: the per-component detail endpoint
// doesn't exist yet, and something that looks pressable but does nothing invites repeated clicking.
// The member feed's identity travels on the element either way, so wiring `onDrillDown` is the only
// change needed to make every marker live.
//
// `isDrillable` says whether pressing will navigate. It does NOT gate the control: a marker whose
// feed can't be resolved stays pressable and opens an explanation instead, because an inert cell
// can't tell "not viewable" apart from "the app is broken". It only decides which action the
// tooltip and the accessible name promise — offering "open the detail" and then not opening it
// would be the worse lie.
export const MemberMarkerCell = ({ input, onDrillDown, isDrillable = true, valueScale = 1 }) => {
  // A row that never mentions this member. The column exists because some other row named the alias,
  // and every row has to fill every column, so this one gets an em dash rather than a blank cell.
  //
  // Not a slim response, despite what this comment used to claim: slim omits `inputs` from every
  // row, `deriveMemberColumns` therefore finds no aliases, and with no member columns there is no
  // marker cell to render. Nothing slim ever reaches this component.
  if (!input) {
    return <StatusText isLowContrast>{EM_DASH}</StatusText>;
  }

  const label = formatMarker(input.status);
  const feedLabel = input.label || formatFeedId(input.feedId);
  // An aggregated member's value spans several occasions, and the per-occasion descriptions describe
  // one — `missing` would even deny the row a combined score it may well have. The status WORD stays,
  // because it is still the honest summary; what changes is the sentence explaining it.
  const description = aggregatedMarkerDescription(input) || MARKER_DESCRIPTIONS[input.status];
  const hasValue = typeof input.value === 'number' && isFinite(input.value);
  const tint = hasValue ? inputValueTint(input.value, valueScale) : null;

  const spoken = hasValue ? `${feedLabel}: ${formatScore(input.value)}, ${label}` : `${feedLabel}: ${label}`;
  const action = isDrillable ? 'Open this component’s detail' : 'Why this component can’t be opened';
  const tooltip = `${feedLabel}: ${hasValue ? `${formatScore(input.value)} — ${label}` : label}. ${
    description || ''
  }`.trim();

  // Stale carries a value but isn't fresh, and the wash can't say that — so it keeps a dot. Present
  // needs none: the colour is the status.
  const staleDot =
    input.status === 'stale' ? <MarkerDot $token={MARKER_TOKENS.stale} data-testid="stale-marker" /> : null;

  if (!hasValue) {
    const statusBody = <Body7 isLowContrast>{label}</Body7>;
    return onDrillDown ? (
      <StatusButton
        onClick={() => onDrillDown(input)}
        data-feed-id={input.feedId}
        title={`${tooltip} ${action}.`}
        aria-label={`${spoken}. ${action}`}
      >
        {statusBody}
      </StatusButton>
    ) : (
      <StatusText as="span" isLowContrast data-feed-id={input.feedId} title={tooltip} aria-label={spoken}>
        {label}
      </StatusText>
    );
  }

  const valueBody = (
    <>
      {staleDot}
      <Body7 bold>{formatScore(input.value)}</Body7>
    </>
  );

  if (!onDrillDown) {
    return (
      <WashCell
        data-feed-id={input.feedId}
        title={tooltip}
        aria-label={spoken}
        $tintToken={tint?.token}
        $tintAlpha={tint?.alpha}
      >
        {valueBody}
      </WashCell>
    );
  }

  return (
    <ValueButton
      type="button"
      onClick={() => onDrillDown(input)}
      data-feed-id={input.feedId}
      title={`${tooltip} ${action}.`}
      aria-label={`${spoken}. ${action}`}
      $tintToken={tint?.token}
      $tintAlpha={tint?.alpha}
    >
      {valueBody}
    </ValueButton>
  );
};
